import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";

import { s3PublicUrl } from "../../utils/s3PublicUrl";

function Login() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const arrowPlayCheckStartedRef = useRef(false);
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData, currentTheme } = useSelector((state) => state.theme);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const fromMobileApp = searchParams.get("fromMobileApp") === "true";
  const hasOtp = (() => {
    const v = searchParams.get("otp");
    return v != null && String(v).trim() !== "";
  })();
  const hasSave = (() => {
    const v = searchParams.get("save");
    return v != null && String(v).trim() !== "";
  })();
  const hasDemo = (() => {
    const v = searchParams.get("demo");
    return v != null && String(v).trim() !== "";
  })();
  const lastLoginKeyRef = useRef("");
  const lastAlreadyPlayedKeyRef = useRef("");

  useEffect(() => {
    if (!fromMobileApp) return;
    try {
      window.sessionStorage.setItem("fromMobileApp", "true");
    } catch {
      /* ignore */
    }
    // Hide back button in mobile app mode.
    dispatch(setBackButtonUrl(null));
  }, [fromMobileApp, dispatch]);

  useEffect(() => {
    // Same as health-sprint: don't dispatch login when there is no trigger in URL.
    if (!hasOtp && !hasSave && !hasDemo && !isAdmin) return;
    const reportId =
      searchParams.get("reportId") ||
      searchParams.get("reportid") ||
      searchParams.get("report_id") ||
      "";
    const otp = searchParams.get("otp") || "";
    const save = searchParams.get("save") || "";
    const demo = searchParams.get("demo") || "";
    const admin = searchParams.get("admin") || "";
    const key = `${otp}|${save}|${demo}|${admin}|${reportId}`;
    if (lastLoginKeyRef.current === key) return;
    lastLoginKeyRef.current = key;
    // When a new login trigger arrives, allow the already-played check to run again.
    arrowPlayCheckStartedRef.current = false;
    dispatch(loginUser(reportId ? { reportId } : {}));
  }, [dispatch, hasOtp, hasSave, hasDemo, isAdmin, searchParams]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user) {
      console.log("user", user);
    }
    if (status === "succeeded" && user.backButtonRedirect) {
      if (!fromMobileApp) dispatch(setBackButtonUrl(user.backButtonRedirect));
    }

  }, [status, user, fromMobileApp, dispatch]);



 useEffect(() => {
  if (status !== "failed") return;
  const msg = error ? String(error) : "Session expired";
  const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
  Swal.fire("Session Expired!", msg, "error").then(() => {
    window.location.href = redirect;
  });
}, [status, error, user]);

  /** Block when latest stage for this theme is `completed`. */
  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || !user?.userId) return;
    // IMPORTANT: `/login?save=true` is used for in-app back navigation.
    // Do not block/redirect from this flow, otherwise the game becomes unusable after finishing a day.
    if (hasSave && !hasOtp) return;
    const alreadyPlayedKey = `${user?.sessionId || ""}&${user?.organizationId || ""}|${user?.userId || ""}|${user?.gameId || ""}`;
    if (lastAlreadyPlayedKeyRef.current !== alreadyPlayedKey) {
      lastAlreadyPlayedKeyRef.current = alreadyPlayedKey;
      arrowPlayCheckStartedRef.current = false;
    }
    if (arrowPlayCheckStartedRef.current) return;
    arrowPlayCheckStartedRef.current = true;

    void (async () => {
      try {
        // `loginSlice` already computes `user.gameover` by reading the report status.
        // If it indicates completion, block replay and show popup.
        const isAlreadyPlayed = Number(user?.gameover ?? 0) > 0;
        if (!isAlreadyPlayed) return;

        const storedFromMobile =
          (() => {
            try {
              return window.sessionStorage.getItem("fromMobileApp") === "true";
            } catch {
              return false;
            }
          })();
        const safeRedirect =
          storedFromMobile || fromMobileApp
            ? process.env.REACT_APP_BASE_URL || "/"
            : user?.backButtonRedirect ||
              process.env.REACT_APP_BASE_URL ||
              "/";
        const redirect =
          String(safeRedirect || "").toLowerCase().includes("logout")
            ? (process.env.REACT_APP_BASE_URL || "/")
            : safeRedirect;
        await Swal.fire("You have already played", "", "error");
        window.location.href = redirect;
      } catch {
        arrowPlayCheckStartedRef.current = false;
      }
    })();
  }, [status, user, isAdmin, adminToken, themeStatus, themeData?.themename, currentTheme]);

  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  const soundEnabled =
    (themeData?.sound_toggle ?? themeData?.sound_toogle) === true ||
    String(themeData?.sound_toggle ?? themeData?.sound_toogle).toLowerCase() === "true";
  const playBeginClick = () => {
    if (!soundEnabled) return;
    const raw = themeData?.default_question_sound;
    if (raw == null || String(raw).trim() === "") return;
    const url = s3PublicUrl(raw);
    if (!url || typeof Audio === "undefined") return;
    try {
      const a = new Audio(url);
      a.volume = 0.92;
      void a.play().catch(() => {});
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="login-main-container">
      {themeData && (
        <img
          src={s3PublicUrl(themeData.logo)}
          className="login-logo"
          alt="Game Logo"
        />
      )}
        {user && status === "succeeded" && (
          <div>
            <button
              type="button"
              className="begin-play-btn"
              onClick={() => {
                playBeginClick();
                const gameMode = themeData?.game_mode === "self_paced" ? "self_paced" : "live";
                navigate(gameMode === "live" ? "/waiting-room" : "/canvas", { replace: true });
              }}
              style={{
                ...(themeData?.box_bg_color && { backgroundColor: themeData.box_bg_color }),
                ...(themeData?.box_text_color && { color: themeData.box_text_color }),
              }}
            >
              BEGIN PLAY
            </button>
          </div>
        )}
    </div>
  );
}

export default Login;
